import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from "framer-motion";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";
import hero from './../../assets/vision/Empowerment.png';
import Header from '../../components/Navbar/Header';
import { Card } from './components/ui/Card';
import { Button } from './components/ui/button';
import { EmpowermentCountUpStats } from './components/ui/EmpowermentCountUpStats';
import BackToVisionButton from './components/ui/BackToVisionButton';
import { ArrowRight } from 'lucide-react';

const IconWrapper = ({ children }) => (
  <div className="rounded-full bg-gray-100 p-3 text-gray-600">
    {children}
  </div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <Card className="flex flex-col items-center text-center p-6">
    {Icon && <Icon className="w-12 h-12 mb-4" />}
    <h3 className="text-xl font-semibold mt-4 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </Card>
);

const Section = ({ title, children, className = "" }) => (
  <section className={`py-16 ${className} mx-2`}>
    <h2 className="text-3xl font-bold text-center mb-8 pr-5 pl-5">{title}</h2>
    {children}
  </section>
);

const empowermentItems = [
  {
    id: 1,
    title: "Breaking Down Barriers",
    description: "We simplify business credit management and connect entrepreneurs to funding opportunities traditionally out of reach. Our AI-powered insights provide tailored strategies and predictive analytics to help businesses adapt and grow with confidence.",
    img: "/vision/emp/emp(1).jpg",
  },
  {
    id: 2,
    title: "Building Resilience",
    description: "From incorporation to scaling, our Knowledge Hub provides actionable, step-by-step guidance. We offer tools to monitor and safeguard your financial health, ensuring you stay in control at every stage of your business journey.",
    img: "/vision/emp/emp(2).jpg",
  },
  {
    id: 3,
    title: "Championing Equity",
    description: "We provide focused support for women- and minority-owned businesses, addressing historic inequities in financial access. By amplifying voices and providing resources, we help every entrepreneur contribute to America's legacy of progress.",
    img: "/vision/emp/emp(3).jpg",
  },
];

const OppoScroll = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  return (
    <>
     
      <section ref={targetRef} className="flex bg-black text-white">
        <Content content={empowermentItems} />
        <Images content={empowermentItems} scrollYProgress={scrollYProgress} />
      </section>
 
    </>
  );
};

const Content = ({ content }) => {
  return (
    <div className="w-full md:w-1/2">
      {content.map(({ id, title, description }, idx) => (
        <div
          key={id}
          className={`p-8 h-screen flex flex-col justify-center ${
            idx % 2 ? "bg-white text-black" : "bg-black text-white"
          }`}
        >
          <h3 className="text-3xl font-medium mb-4">{title}</h3>
          <p className="font-light w-full max-w-md">{description}</p>
        </div>
      ))}
    </div>
  );
};

const Images = ({ content, scrollYProgress }) => {
  const top = useTransform(
    scrollYProgress,
    [0, 1],
    [`-${(content.length - 1) * 100}vh`, "0vh"]
  );

  return (
    <div className="h-screen overflow-hidden sticky top-0 w-24 md:w-1/2 hidden md:block">
      <motion.div style={{ top }} className="absolute left-0 right-0">
        {[...content].reverse().map(({ img, id, title }) => (
          <img
            key={id}
            alt={title}
            className="h-screen w-full object-cover"
            src={img}
          />
        ))}
      </motion.div>
    </div>
  );
};

const Empowerment = () => {
  return (
    <div className="min-h-screen bg-white text-black">
     
      <Header
      />
      <main>
 
        {/* Hero Section */}
     
        <section
          className="pt-32 pb-16 bg-cover bg-center text-white"
          style={{
            backgroundImage: `url(${hero})`,
          }}
        >
     
          <div className="text-center px-4">
            <h1 className="text-5xl font-bold mb-10 mt-40 text-white">
              Empowering America's Small Businesses
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
              At FuteurAI, we're not just building tools—we're building bridges. Bridges to opportunity, equality, and a thriving future for the 34.8 million small businesses that are the backbone of America's economy.
            </p>
        
                <div className="fixed top-20 left-4 z-40">
        <BackToVisionButton />
      </div>
          </div>
        </section>
        <Section title="Our Mission: Empowerment for All">
          <div className="max-w-3xl mx-auto text-center pr-5 pl-5"> 
            <p className="text-gray-600 ">
              Small businesses are America's foundation. They create jobs, drive innovation, and bring communities to life. Yet, systemic challenges—inequity, limited access to resources, and a lack of actionable guidance—hinder their potential.
            </p>
            <p className="text-gray-600 mt-4">
              At FuteurAI, we see these barriers as opportunities to lead change. Empowerment isn't just a value; it's our purpose.
            </p>
            <p className="text-gray-600 mt-4">
              Our commitment is simple yet profound: To provide every small business with the tools, knowledge, and support they need to thrive—regardless of size, industry, or background.
            </p>
          </div>
        </Section>

        <Section title="How We Empower">
          <div className="max-w-3xl mx-auto text-center mb-12 pr-5 pl-5">
            <p className="text-gray-600">
              At FuteurAI, we understand that empowerment isn't just a feature—it's a responsibility. We're committed to building an inclusive platform that empowers all users, regardless of their abilities, background, or circumstances.
            </p>
          </div>
          <OppoScroll />
        </Section>
<Section
  title={
    <>
      The Meaning of{' '}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30" // Adjust this to match title size
        height="30"
        viewBox="50 90 60 130"
        className="inline-block align-middle font-bold"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="m 69.039047,97.324219 q -5.800781,0 -10.019531,0.703125 -4.21875,0.615234 -7.382813,2.724606 -3.076171,2.02149 -5.273437,5.97657 -2.109375,3.86718 -3.867188,10.2832 H 40.562485 V 90.820312 h 88.681645 v 3.251954 h -4.21875 q -4.30665,0 -8.08594,2.109375 -1.58203,0.878906 -2.54883,1.845703 -0.87891,0.966797 -1.40625,2.724606 -0.43945,1.66993 -0.61523,4.30664 -0.17579,2.63672 -0.17579,6.85547 v 76.99219 q 0,10.81055 2.10938,13.53516 3.25195,4.30664 10.72266,4.30664 h 4.21875 V 210 H 78.091782 v -3.25195 h 4.30664 q 4.21875,0 8.085938,-2.10938 1.582031,-0.8789 2.548828,-1.8457 0.966797,-1.05469 1.40625,-2.72461 0.527344,-1.66992 0.703125,-4.30664 0.175781,-2.63672 0.175781,-6.85547 V 151.46484 H 73.60936 q -4.394532,0 -7.382813,0.9668 -2.988281,0.87891 -4.746094,2.90039 -1.757812,1.93359 -2.548828,5.18555 -0.791015,3.16406 -0.878906,7.64648 h -3.251953 v -40.34179 h 3.251953 q 0.615234,5.00976 1.669922,8.17382 1.142578,3.16407 2.988281,5.00977 1.845703,1.8457 4.482422,2.54883 2.724609,0.61523 6.416016,0.61523 H 95.318344 V 97.324219 Z"
          style={{ fill: '#000000' }}
        />
      </svg>
    </>
  }
>
  <div className="max-w-3xl mx-auto text-center pr-5 pl-5">
    <p className="text-gray-600 mb-6">
      Our logo, the reversed F (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="15" // Adjust this to match paragraph font size
        height="15"
        viewBox="40 90 60 120"
        className="inline-block align-middle"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="m 69.039047,97.324219 q -5.800781,0 -10.019531,0.703125 -4.21875,0.615234 -7.382813,2.724606 -3.076171,2.02149 -5.273437,5.97657 -2.109375,3.86718 -3.867188,10.2832 H 40.562485 V 90.820312 h 88.681645 v 3.251954 h -4.21875 q -4.30665,0 -8.08594,2.109375 -1.58203,0.878906 -2.54883,1.845703 -0.87891,0.966797 -1.40625,2.724606 -0.43945,1.66993 -0.61523,4.30664 -0.17579,2.63672 -0.17579,6.85547 v 76.99219 q 0,10.81055 2.10938,13.53516 3.25195,4.30664 10.72266,4.30664 h 4.21875 V 210 H 78.091782 v -3.25195 h 4.30664 q 4.21875,0 8.085938,-2.10938 1.582031,-0.8789 2.548828,-1.8457 0.966797,-1.05469 1.40625,-2.72461 0.527344,-1.66992 0.703125,-4.30664 0.175781,-2.63672 0.175781,-6.85547 V 151.46484 H 73.60936 q -4.394532,0 -7.382813,0.9668 -2.988281,0.87891 -4.746094,2.90039 -1.757812,1.93359 -2.548828,5.18555 -0.791015,3.16406 -0.878906,7.64648 h -3.251953 v -40.34179 h 3.251953 q 0.615234,5.00976 1.669922,8.17382 1.142578,3.16407 2.988281,5.00977 1.845703,1.8457 4.482422,2.54883 2.724609,0.61523 6.416016,0.61523 H 95.318344 V 97.324219 Z"
          style={{ fill: '#000000' }}
        />
      </svg>
      ), represents more than our brand—it embodies our mission. Derived from Latin script, it symbolizes "filia" and "femina," honoring the strength, resilience, and leadership of women and underserved communities.
    </p>
    <p className="text-gray-600">
      Just as the  <svg
        xmlns="http://www.w3.org/2000/svg"
        width="15" // Adjust this to match paragraph font size
        height="15"
        viewBox="40 90 95 120"
        className="inline-block align-middle"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="m 69.039047,97.324219 q -5.800781,0 -10.019531,0.703125 -4.21875,0.615234 -7.382813,2.724606 -3.076171,2.02149 -5.273437,5.97657 -2.109375,3.86718 -3.867188,10.2832 H 40.562485 V 90.820312 h 88.681645 v 3.251954 h -4.21875 q -4.30665,0 -8.08594,2.109375 -1.58203,0.878906 -2.54883,1.845703 -0.87891,0.966797 -1.40625,2.724606 -0.43945,1.66993 -0.61523,4.30664 -0.17579,2.63672 -0.17579,6.85547 v 76.99219 q 0,10.81055 2.10938,13.53516 3.25195,4.30664 10.72266,4.30664 h 4.21875 V 210 H 78.091782 v -3.25195 h 4.30664 q 4.21875,0 8.085938,-2.10938 1.582031,-0.8789 2.548828,-1.8457 0.966797,-1.05469 1.40625,-2.72461 0.527344,-1.66992 0.703125,-4.30664 0.175781,-2.63672 0.175781,-6.85547 V 151.46484 H 73.60936 q -4.394532,0 -7.382813,0.9668 -2.988281,0.87891 -4.746094,2.90039 -1.757812,1.93359 -2.548828,5.18555 -0.791015,3.16406 -0.878906,7.64648 h -3.251953 v -40.34179 h 3.251953 q 0.615234,5.00976 1.669922,8.17382 1.142578,3.16407 2.988281,5.00977 1.845703,1.8457 4.482422,2.54883 2.724609,0.61523 6.416016,0.61523 H 95.318344 V 97.324219 Z"
          style={{ fill: '#000000' }}
        />
      </svg>  turns the norm on its head, FuteurAI is redefining the narrative for small businesses, creating a future where every entrepreneur has the tools to thrive.
    </p>
  </div>
</Section>

        <Section title="Impact: The Ripple Effect of Empowerment">
          <EmpowermentCountUpStats />
        </Section>

        <Section title="Join the Movement" className="bg-gray-50">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-gray-600 italic">
              "Empowerment isn't just about lifting individuals—it's about uplifting communities, strengthening economies, and honoring the legacy of resilience that defines America. Together, we'll build a future where every small business has the power to lead, thrive, and shape tomorrow."
            </p>
            <p className="text-gray-600 mt-4">
              - Team Futeur
            </p>
          </div>
          <div className="text-center">
             <Button 
            size="lg" 
            variant="default" 
            className="group" 
            onClick={() => {
              window.location.href = 'https://app.futeur.ai/signup';
              setTimeout(() => window.scrollTo(0, 0), 0); // Ensure the scroll happens after navigation
            }}
          >
          Start Empowering Your Business Today
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          </div>
        </Section>
      </main>
    </div>
  );
};

export default Empowerment;

